import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";
import {RequestState, ShareType, SocialNetwork} from "data/enums";
import {Bindings} from "data/constants/bindings";
import {share} from "data/utils";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUser, IUserStore} from "data/stores/user/user.store";
import type {IMenuStore} from "data/stores/menu/menu.store";

export interface IHeaderController extends ViewController {
	get isAuthorized(): boolean;
	get user(): IUser | undefined;
	get i18n(): ILocalizationStore;
	get isOpenMenu(): boolean;
	get isMobileShareVisible(): boolean;

	shareViaFB: () => void;
	shareViaTW: () => void;
	shareViaMobile: () => void;
	toggleMenu: () => void;
	closeMenu: () => void;
}

@injectable()
export class HeaderController implements IHeaderController {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable private _isNarrowScreen: boolean = false;

	get isAuthorized() {
		return this._userStore.isAuthorized;
	}

	get user() {
		return this._userStore.user;
	}

	get i18n() {
		return this._i18nStore;
	}

	get isOpenMenu(): boolean {
		return this._menuStore.isOpenMenu;
	}

	get isMobileShareVisible() {
		return "share" in navigator && this._isNarrowScreen;
	}

	private get narrowScreenWatcher() {
		return window.matchMedia("(max-width: 960px)");
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.MenuStore) private _menuStore: IMenuStore
	) {
		makeAutoObservable(this);
	}

	toggleMenu = () => {
		this._menuStore.toggleMenu();
	};

	closeMenu = () => {
		this._menuStore.closeMenu();
	};

	private share(socialNetwork: SocialNetwork) {
		share({
			socialNetwork,
			type: ShareType.General,
			message: this.i18n.t(
				"share.general.message",
				"I’m playing Ryder Cup Fantasy - come and take me on by picking six golfers each day and compete to win a trip to New York for the 2025 Ryder Cup!"
			),
		});
	}

	shareViaFB = () => this.share(SocialNetwork.Facebook);

	shareViaTW = () => this.share(SocialNetwork.Twitter);

	shareViaMobile = () => this.share(SocialNetwork.Mobile);

	@action private updateNarrowScreenFlag = () => {
		this._isNarrowScreen = this.narrowScreenWatcher.matches;
	};

	dispose(): void {
		this.narrowScreenWatcher.removeEventListener("change", this.updateNarrowScreenFlag);
	}

	init(): void {
		this.updateNarrowScreenFlag();
		this.narrowScreenWatcher.addEventListener("change", this.updateNarrowScreenFlag);
	}
}
